import type { RoleInfo, Permission, UserRole, RolePermissions } from '../types/Auth';

// Role hierarchy (lower number = higher access)
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  admin: 0,
  system_operator: 1,
  product_admin: 2,
  reinsurer: 3,
  user: 4,
};

// Complete role definitions with permissions
export const ROLE_DEFINITIONS: Record<UserRole, RoleInfo> = {
  admin: {
    role: 'admin',
    label: 'Administrator',
    description: 'Full system access with all permissions',
    hierarchy: ROLE_HIERARCHY.admin,
    permissions: [
      // User Management
      'users:create',
      'users:read',
      'users:update',
      'users:delete',
      'users:assign_roles',
      // Organization Management
      'organizations:create',
      'organizations:read',
      'organizations:update',
      'organizations:delete',
      'organizations:manage_members',
      // Farmer Management
      'farmers:create',
      'farmers:read',
      'farmers:update',
      'farmers:delete',
      // Product Management
      'products:create',
      'products:read',
      'products:update',
      'products:delete',
      'products:configure',
      // Quote Management
      'quotes:create',
      'quotes:read',
      'quotes:update',
      'quotes:delete',
      'quotes:approve',
      // Payment Management
      'payments:read',
      'payments:update',
      'payments:admin',
      // Policy Management
      'policies:create',
      'policies:read',
      'policies:update',
      'policies:issue',
      'policies:cancel',
      // Claims Management
      'claims:create',
      'claims:read',
      'claims:update',
      'claims:approve',
      'claims:payout',
      // System Settings
      'system:configure',
      'system:monitor',
      'system:audit',
      'system:backup',
      // Reporting
      'reports:view',
      'reports:export',
      'reports:admin',
      // Role Management
      'roles:create',
      'roles:write',
      'roles:delete',
      // Permission Management
      'permissions:write',
      // Ledger Management
      'ledger:read',
      'ledger:write',
      'ledger:reverse',
      'ledger:reports',
      'ledger:admin',
      // Notifications
      'notifications:send',
      'notifications:send_sms',
      'notifications:send_email',
      'notifications:view',
      // Note: Navigation permissions (topnav:* and nav:*) loaded from backend database
    ],
  },
  
  system_operator: {
    role: 'system_operator',
    label: 'System Operator',
    description: 'Technical administration and system monitoring',
    hierarchy: ROLE_HIERARCHY.system_operator,
    permissions: [
      // Limited User Management
      'users:read',
      'users:update',
      // Organization Management
      'organizations:read',
      'organizations:update',
      // Farmer Management (read only)
      'farmers:read',
      // System Settings
      'system:configure',
      'system:monitor',
      'system:audit',
      'system:backup',
      // Reporting
      'reports:view',
      'reports:export',
      'reports:admin',
      // Read access to most entities
      'products:read',
      'quotes:read',
      'payments:read',
      'payments:update',
      'policies:read',
      'claims:read',
      // Ledger Management (read only)
      'ledger:read',
      'ledger:reports',
      // Notifications
      'notifications:send',
      'notifications:send_sms',
      'notifications:send_email',
      'notifications:view',
    ],
  },

  product_admin: {
    role: 'product_admin',
    label: 'Product Administrator',
    description: 'Product and quote management access',
    hierarchy: ROLE_HIERARCHY.product_admin,
    permissions: [
      // Product Management
      'products:create',
      'products:read',
      'products:update',
      'products:delete',
      'products:configure',
      // Quote Management
      'quotes:create',
      'quotes:read',
      'quotes:update',
      'quotes:delete',
      'quotes:approve',
      // Payment Management
      'payments:read',
      'payments:update',
      // Policy Management (limited)
      'policies:read',
      'policies:issue',
      // Limited User Management
      'users:read',
      'users:create',  // Issue #93: Product admin should be able to add new users
      // Organization Management (limited)
      'organizations:read',
      'organizations:update',  // Issue #108: Product admin should be able to edit org type/status
      // Farmer Management
      'farmers:create',
      'farmers:read',
      'farmers:update',
      'farmers:delete',
      // Reporting
      'reports:view',
      'reports:export',
      // Ledger Management (read only)
      'ledger:read',
      'ledger:reports',
    ],
  },

  reinsurer: {
    role: 'reinsurer',
    label: 'Reinsurer',
    description: 'Claims approval and risk management access',
    hierarchy: ROLE_HIERARCHY.reinsurer,
    permissions: [
      // Claims Management
      'claims:read',
      'claims:update',
      'claims:approve',
      'claims:payout',
      // Policy Management (read-only)
      'policies:read',
      // Quote Management (read-only)
      'quotes:read',
      // Payment Management (read-only)
      'payments:read',
      // Product Management (read-only)
      'products:read',
      // Farmer Management (read-only)
      'farmers:read',
      // Reporting
      'reports:view',
      'reports:export',
    ],
  },

  user: {
    role: 'user',
    label: 'User',
    description: 'Standard user access with limited permissions',
    hierarchy: ROLE_HIERARCHY.user,
    permissions: [
      // Basic access - mostly read-only
      'users:read', // Can view own profile
      'organizations:read', // Can view own organization
      'farmers:read',
      'products:read',
      'quotes:read',
      'policies:read',
      'claims:read',
      'reports:view',
    ],
  },
};

// Role permissions mapping for easy lookup
export const ROLE_PERMISSIONS: RolePermissions = Object.fromEntries(
  Object.entries(ROLE_DEFINITIONS).map(([role, info]) => [role, info.permissions])
);

// Permission groups for easier management
export const PERMISSION_GROUPS = {
  USER_MANAGEMENT: [
    'users:create',
    'users:read',
    'users:update', 
    'users:delete',
    'users:assign_roles',
  ] as Permission[],
  
  ORGANIZATION_MANAGEMENT: [
    'organizations:create',
    'organizations:read',
    'organizations:update',
    'organizations:delete',
    'organizations:manage_members',
  ] as Permission[],
  
  FARMER_MANAGEMENT: [
    'farmers:create',
    'farmers:read',
    'farmers:update',
    'farmers:delete',
  ] as Permission[],
  
  PRODUCT_MANAGEMENT: [
    'products:create',
    'products:read',
    'products:update',
    'products:delete',
    'products:configure',
  ] as Permission[],
  
  QUOTE_MANAGEMENT: [
    'quotes:create',
    'quotes:read',
    'quotes:update',
    'quotes:delete',
    'quotes:approve',
  ] as Permission[],
  
  PAYMENT_MANAGEMENT: [
    'payments:read',
    'payments:update',
    'payments:admin',
  ] as Permission[],
  
  POLICY_MANAGEMENT: [
    'policies:create',
    'policies:read',
    'policies:update',
    'policies:issue',
    'policies:cancel',
  ] as Permission[],
  
  CLAIMS_MANAGEMENT: [
    'claims:create',
    'claims:read',
    'claims:update',
    'claims:approve',
    'claims:payout',
  ] as Permission[],
  
  SYSTEM_SETTINGS: [
    'system:configure',
    'system:monitor',
    'system:audit',
    'system:backup',
  ] as Permission[],
  
  REPORTING: [
    'reports:view',
    'reports:export',
    'reports:admin',
  ] as Permission[],
  
  ROLES_AND_PERMISSIONS: [
    'roles:read',
    'roles:create',
    'roles:write',
    'roles:delete',
    'permissions:read',
    'permissions:write',
  ] as Permission[],
  
  LEDGER_MANAGEMENT: [
    'ledger:read',
    'ledger:write',
    'ledger:reverse',
    'ledger:reports',
    'ledger:admin',
  ] as Permission[],
  
  NOTIFICATIONS: [
    'notifications:send',
    'notifications:send_sms',
    'notifications:send_email',
    'notifications:view',
  ] as Permission[],
  
  // Navigation Permissions - Top Navigation
  TOP_NAVIGATION: [
    'topnav:dashboards',
    'topnav:clients-policies',
    'topnav:users-organizations',
    'topnav:accounting-finance',
    'topnav:products-templates',
    'topnav:system-admin',
    'topnav:development',
  ] as Permission[],
  
  // Navigation Permissions - Sidebar Items
  NAV_DASHBOARDS: [
    'nav:dashboard',
    'nav:analytics-dashboard',
  ] as Permission[],
  
  NAV_CLIENTS_POLICIES: [
    'nav:farmers',
    'nav:quotes',
    'nav:claims',
    'nav:notifications',
  ] as Permission[],
  
  NAV_USERS_ORGANIZATIONS: [
    'nav:organizations',
    'nav:users',
  ] as Permission[],
  
  NAV_ACCOUNTING_FINANCE: [
    'nav:ledger',
    'nav:journals',
    'nav:chart-of-accounts',
    'nav:periods',
    'nav:payment-management',
    'nav:ledger-reports',
  ] as Permission[],
  
  NAV_PRODUCTS_TEMPLATES: [
    'nav:products',
    'nav:coverage',
    'nav:document-templates',
    'nav:shapefiles',
    'nav:administrative-units',
    'nav:files',
  ] as Permission[],
  
  NAV_SYSTEM_ADMIN: [
    'nav:roles',
    'nav:settings',
    'nav:database',
    'nav:jobs',
    'nav:monitoring',
    'nav:health-check',
    'nav:audit',
  ] as Permission[],
  
  NAV_DEVELOPMENT: [
    'nav:mock-payment',
    'nav:batch-management',
    'nav:state-demo',
    'nav:process-manager',
  ] as Permission[],
};

// Helper to get all roles as options for dropdowns
export const ROLE_OPTIONS = Object.values(ROLE_DEFINITIONS).map(role => ({
  value: role.role,
  label: role.label,
  description: role.description,
}));

// Helper to check if a role has higher or equal access than another
export const hasHigherOrEqualAccess = (userRole: UserRole, requiredRole: UserRole): boolean => {
  return ROLE_HIERARCHY[userRole] <= ROLE_HIERARCHY[requiredRole];
};

// Helper to get role display name
export const getRoleDisplayName = (role: UserRole): string => {
  return ROLE_DEFINITIONS[role]?.label || role;
};

// Helper to get role description
export const getRoleDescription = (role: UserRole): string => {
  return ROLE_DEFINITIONS[role]?.description || '';
}; 